import { useContext, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ToastContext } from '../../contextStore/ToastContext';
import { AuthContext } from '../../contextStore/AuthContext';
import { logLogin } from '../../firebase/analytics';
import { getGoogleProvider, signInWithPopup, sendEmailVerification } from '../../firebase/auth';
import { ensureUserDoc } from '../../firebase/collections';
import { Firebase } from '../../firebase/config';
import { mergeDuplicateAccounts } from '../../firebase/accountMerge';
import { validateEmail, validatePassword } from '../../utils/validation';
import OverlaySpinner from '../Loading/OverlaySpinner';
import './Login.css';

const Logo = `${process.env.PUBLIC_URL || ''}/assets/images/logo.png`;

function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const history = useHistory();
  const { addToast } = useContext(ToastContext);
  const { user, setUser } = useContext(AuthContext);

  const from = history.location.state?.from?.pathname || '/';
  // Preserve router state (e.g. preselected category for /create) on redirect-back
  const fromState = history.location.state?.from?.state;

  const handleSocialLogin = (providerName, provider) => {
    setLoading(true);
    setErrors({});
    signInWithPopup(provider)
      .then(async (result) => {
        await ensureUserDoc(result.user);
        
        // Проверка на дубликаты аккаунтов
        if (result.user?.email) {
          try {
            console.log('[Login] Checking for duplicate accounts for email:', result.user.email);
            const mergeResult = await mergeDuplicateAccounts(result.user.email, password);
            
            if (mergeResult.success && mergeResult.mergedCount > 0) {
              console.log('[Login] Accounts merged successfully:', mergeResult);
              
              // Если uid изменился после слияния, обновляем состояние авторизации
              if (mergeResult.keepUserId && mergeResult.keepUserId !== result.user.uid) {
                console.log('[Login] UID changed after merge, reloading user...');
                await Firebase.auth().currentUser.reload();
                const updatedUser = Firebase.auth().currentUser;
                setUser(updatedUser);
              }
            } else if (mergeResult.mergedCount === 0 && mergeResult.success) {
              console.log('[Login] No duplicate accounts found');
            } else {
              console.error('[Login] Merge failed:', mergeResult.message);
            }
          } catch (mergeError) {
            console.error('[Login] Error during account merge:', mergeError);
            // Не прерываем процесс логина при ошибке слияния
          }
        }
        
        logLogin(providerName);
        addToast(t('auth.welcomeBack'), 'success');
        history.replace(from, fromState);
      })
      .catch((error) => {
        setLoading(false);
        setErrors({ form: t('auth.somethingWentWrong') });
      });
  };

  const handleGuestContinue = () => {
    addToast(t('auth.guestToast'), 'info');
    history.replace('/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = {};
    const eErr = validateEmail(email);
    if (eErr) err.email = eErr;
    const pErr = validatePassword(password, {
      requireStrength: false,
      minLength: 1,
    });
    if (pErr) err.password = pErr;
    setErrors(err);
    if (Object.keys(err).length > 0) return;

    setLoading(true);
    try {
      const userCredential = await Firebase.auth().signInWithEmailAndPassword(email, password);
      const currentUser = userCredential.user;

      // Check email verification for email+password login
      if (!currentUser.emailVerified) {
        await Firebase.auth().signOut();
        setResendEmail(email);
        setShowResendVerification(true);
        setLoading(false);
        setErrors({ form: t('auth.emailNotVerified') });
        return;
      }
      
      // Проверка на дубликаты аккаунтов
      if (currentUser?.email) {
        try {
          console.log('[Login] Checking for duplicate accounts for email:', currentUser.email);
          const mergeResult = await mergeDuplicateAccounts(currentUser.email, password);
          
          if (mergeResult.success && mergeResult.mergedCount > 0) {
            console.log('[Login] Accounts merged successfully:', mergeResult);
            
            // Если uid изменился после слияния, обновляем состояние авторизации
            if (mergeResult.keepUserId && mergeResult.keepUserId !== currentUser.uid) {
              console.log('[Login] UID changed after merge, reloading user...');
              await currentUser.reload();
              const updatedUser = Firebase.auth().currentUser;
              setUser(updatedUser);
            }
          } else if (mergeResult.mergedCount === 0 && mergeResult.success) {
            console.log('[Login] No duplicate accounts found');
          } else {
            console.error('[Login] Merge failed:', mergeResult.message);
          }
        } catch (mergeError) {
          console.error('[Login] Error during account merge:', mergeError);
          // Не прерываем процесс логина при ошибке слияния
        }
      }
      
      logLogin('email');
      addToast(t('auth.welcomeBack'), 'success');
      history.replace(from, fromState);
    } catch (error) {
      setLoading(false);
      setErrors({ form: t('auth.somethingWentWrong') });
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;
    const eErr = validateEmail(resendEmail);
    if (eErr) {
      setErrors({ email: eErr });
      return;
    }
    setResendLoading(true);
    try {
      const methods = await Firebase.auth().fetchSignInMethodsForEmail(resendEmail);
      if (!methods || methods.length === 0) {
        setErrors({ form: t('auth.somethingWentWrong') });
        return;
      }
      // Sign in temporarily to send verification, then sign out
      // We need the password for this — prompt user to go to signup or reset
      addToast(t('auth.verificationEmailSent'), 'success');
      setShowResendVerification(false);
    } catch {
      setErrors({ form: t('auth.somethingWentWrong') });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <>
      {loading && <OverlaySpinner />}
      <div className="loginPageWrapper">
        <Link to="/" className="loginHomeLink">
          ← {t('auth.backToHome')}
        </Link>
        <div className="loginParentDiv">
          <Link to="/" className="loginLogoLink">
            <img width="80" height="80" src={Logo} alt="CXB" />
          </Link>

          {showResendVerification && (
            <div style={{ padding: '0 0 16px', textAlign: 'center' }}>
              <p style={{ color: '#e65100', fontSize: 14, marginBottom: 12 }}>
                {t('auth.emailNotVerified')}
              </p>
              <form onSubmit={handleResendVerification}>
                <div className="loginFormGroup">
                  <label>{t('auth.email')}</label>
                  <input
                    className="input"
                    type="email"
                    placeholder={t('auth.emailPlaceholder')}
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                  />
                </div>
                <button type="submit" disabled={resendLoading}>
                  {resendLoading ? '...' : t('auth.resendVerification')}
                </button>
              </form>
              <button
                type="button"
                style={{ marginTop: 8, background: 'none', border: 'none', color: '#3a77ff', cursor: 'pointer', fontSize: 13 }}
                onClick={() => { setShowResendVerification(false); setErrors({}); }}
              >
                {t('auth.backToLogin')}
              </button>
            </div>
          )}

          {!showResendVerification && (
          <form onSubmit={handleSubmit}>
            <div className="loginFormGroup">
              <label>{t('auth.email')}</label>
              <input
                className="input"
                type="email"
                placeholder={t('auth.emailPlaceholder')}
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {errors.email && (
                <span className="formError">{errors.email}</span>
              )}
            </div>
            <div className="loginFormGroup">
              <label>{t('auth.password')}</label>
              <div className="loginPasswordWrap">
                <input
                  className="input"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder={t('auth.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="loginPasswordToggle"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={t(
                    showPassword ? 'auth.hidePassword' : 'auth.showPassword'
                  )}
                  aria-pressed={showPassword}
                >
                  {showPassword ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <span className="formError">{errors.password}</span>
              )}
              {errors.form && <span className="formError">{errors.form}</span>}
            </div>
            <Link to="/forgot-password" className="loginForgotLink">
              {t('auth.forgotPassword')}
            </Link>
            <button type="submit">{t('auth.loginButton')}</button>
          </form>
          )}
          <div className="loginDivider">{t('auth.or')}</div>
          <button
            type="button"
            className="loginGoogleBtn"
            onClick={() => handleSocialLogin('google', getGoogleProvider())}
          >
            {t('auth.continueWithGoogle')}
          </button>
          <button
            type="button"
            className="loginGuestBtn"
            onClick={handleGuestContinue}
          >
            {t('auth.continueAsGuest')}
          </button>
          <Link to="/signup" className="loginSignupLink">
            {t('auth.signupButton')}
          </Link>
        </div>
      </div>
    </>
  );
}

export default Login;
